import { useEffect, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ChefHat,
  Coffee,
  LayoutDashboard,
  LockKeyhole,
  Menu,
  QrCode,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  Store,
  Users,
  Wallet,
  X,
  Zap,
} from "lucide-react";
import { BrandMark } from "../components/BrandMark";
import type { Me } from "../lib/api";

const problems = [
  {
    icon: Users,
    title: "Antrean di kasir",
    text: "Pelanggan menunggu giliran untuk memesan, sementara meja dan staf sibuk mencatat ulang.",
  },
  {
    icon: ReceiptText,
    title: "Hitung manual saat tutup",
    text: "Stock opname dan pencocokan order keluar dengan uang di tangan makan waktu setiap malam.",
  },
  {
    icon: Wallet,
    title: "Selisih yang tak jelas",
    text: "Uang tidak cocok, order hilang, dan tidak ada jejak yang bisa ditelusuri dengan tenang.",
  },
  {
    icon: LockKeyhole,
    title: "Omzet bukan privasi owner",
    text: "Angka penjualan terlihat oleh banyak orang, padahal seharusnya hanya owner yang tahu.",
  },
];

const features = [
  {
    icon: QrCode,
    title: "QR per meja & kamar",
    text: "Satu QR untuk satu meja atau kamar. Pelanggan memindai, memilih menu, dan memesan tanpa perlu install aplikasi.",
  },
  {
    icon: Zap,
    title: "Order langsung ke dapur",
    text: "Pesanan masuk real-time ke kitchen display dan pelayan. Tidak ada lagi catatan kertas yang berpindah tangan.",
  },
  {
    icon: Wallet,
    title: "QRIS ke rekening owner",
    text: "Pembayaran QRIS masuk langsung ke rekening owner. Dana tidak ditahan, tidak dipotong, dan tidak menunggu settlement.",
  },
  {
    icon: LockKeyhole,
    title: "Penghasilan tetap privat",
    text: "Omzet hanya diketahui owner. Staf kasir tidak melihat angka penjualan, sehingga privasi usaha tetap terjaga.",
  },
  {
    icon: BarChart3,
    title: "Jumlah & total order",
    text: "Di menu owner langsung terlihat berapa order hari ini dan berapa total nilainya — tanpa perlu bertanya ke kasir.",
  },
  {
    icon: Store,
    title: "Siap multi-outlet",
    text: "Kelola beberapa cafe dan kost dari satu akun. Menu, meja, staf, dan laporan dipisah rapi per outlet.",
  },
];

const steps = [
  {
    title: "Daftar & buat outlet",
    text: "Buat akun, isi nama usaha, lalu siapkan outlet pertama. Tidak perlu kartu kredit atau dokumen berbelit.",
  },
  {
    title: "Atur menu & meja",
    text: "Masukkan menu, harga, kategori, lalu buat QR untuk setiap meja atau kamar.",
  },
  {
    title: "Hubungkan QRIS",
    text: "Tautkan merchant QRIS milik owner. Pembayaran pelanggan langsung masuk ke rekening owner.",
  },
  {
    title: "Pesanan mengalir sendiri",
    text: "Pelanggan memesan dari meja, dapur menerima order, dan owner melihat ringkasan penjualan secara real-time.",
  },
];

const audiences = [
  {
    icon: Coffee,
    title: "Cafe & kedai",
    text: "Cocok untuk cafe kecil, kedai kopi, dan resto rumahan yang ingin melayani lebih banyak pelanggan tanpa menambah antrean di kasir.",
    points: [
      "Pelanggan pesan sendiri dari meja",
      "Dapur menerima order real-time",
      "Omzet hanya diketahui owner",
    ],
  },
  {
    icon: LayoutDashboard,
    title: "Kost & hunian",
    text: "Penghuni atau tamu dapat memesan dari kamar atau area umum, sementara pengelola menerima pesanan dan pembayaran di satu tempat.",
    points: [
      "QR per kamar atau area",
      "Pesanan masuk ke pengelola",
      "Pembayaran QRIS tercatat otomatis",
    ],
  },
  {
    icon: ChefHat,
    title: "Multi-outlet",
    text: "Satu owner, beberapa lokasi. Pantau pesanan dan penjualan tiap outlet tanpa berpindah akun atau merekap manual.",
    points: [
      "Satu akun untuk semua outlet",
      "Laporan per outlet",
      "Peran staf yang terpisah",
    ],
  },
];

export function LandingPage({ me }: { me: Me | null }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, []);

  function handleHomeClick(event: MouseEvent<HTMLAnchorElement>) {
    setOpen(false);
    if (window.location.pathname === "/") {
      event.preventDefault();
      if (window.location.hash) {
        window.history.replaceState(null, "", "/");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  return (
    <div className="lp">
      <header className="lp-nav">
        <div className={`lp-container lp-nav__inner${open ? " is-open" : ""}`}>
          <Link
            to="/"
            className="lp-brand"
            aria-label="Cafe System Control"
            onClick={handleHomeClick}
          >
            <BrandMark />
            <span>
              <strong>Cafe System Control</strong>
              <small>Platform pemesanan cafe &amp; kost</small>
            </span>
          </Link>

          <button
            type="button"
            className="lp-nav__toggle"
            aria-expanded={open}
            aria-label={open ? "Tutup menu" : "Buka menu"}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>

          <nav className="lp-nav__links" aria-label="Navigasi utama">
            <a href="#masalah" onClick={() => setOpen(false)}>
              Masalah
            </a>
            <a href="#fitur" onClick={() => setOpen(false)}>
              Fitur
            </a>
            <a href="#alur" onClick={() => setOpen(false)}>
              Cara Kerja
            </a>
            <a href="#untuk-siapa" onClick={() => setOpen(false)}>
              Untuk Siapa
            </a>
          </nav>

          <div className="lp-nav__actions">
            {me ? (
              <Link to="/app" className="btn btn-primary btn--sm">
                Buka Workspace <ArrowRight size={15} />
              </Link>
            ) : (
              <>
                <Link to="/login" className="btn btn-secondary btn--sm">
                  Masuk
                </Link>
                <Link to="/register" className="btn btn-primary btn--sm">
                  Daftar Gratis
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main>
        <section className="lp-hero">
          <div className="lp-container lp-hero__grid">
            <div className="lp-hero__copy">
              <span className="lp-eyebrow">
                <Sparkles size={13} />
                Platform pesanan untuk cafe &amp; kost
              </span>
              <h1>
                Meja terisi.
                <br />
                Pesanan rapi.
                <br />
                <em>Owner tenang.</em>
              </h1>
              <p className="lp-hero__lead">
                QR di meja, pesanan mandiri, pembayaran QRIS langsung ke rekening
                owner, dan antrean dapur yang rapi dalam satu alur. Tanpa antrean
                kasir, tanpa stock opname, tanpa omzet yang diketahui banyak
                orang.
              </p>
              <div className="lp-hero__actions">
                {me ? (
                  <Link to="/app" className="btn btn-primary">
                    Buka Workspace <ArrowRight size={16} />
                  </Link>
                ) : (
                  <Link to="/register" className="btn btn-primary">
                    Mulai Gratis <ArrowRight size={16} />
                  </Link>
                )}
                <a href="#alur" className="btn btn-secondary">
                  Lihat Cara Kerja
                </a>
              </div>
              <div className="lp-hero__badges">
                <span>
                  <CheckCircle2 size={14} /> Tanpa antrean kasir
                </span>
                <span>
                  <CheckCircle2 size={14} /> Omzet privat untuk owner
                </span>
                <span>
                  <CheckCircle2 size={14} /> QRIS langsung ke rekening owner
                </span>
              </div>
            </div>

            <div className="lp-hero__mock" aria-hidden="true">
              <div className="lp-mock__top">
                <div>
                  <span className="lp-mock__eyebrow">Ringkasan hari ini</span>
                  <strong>Omzet</strong>
                </div>
                <span className="lp-mock__badge">
                  <LockKeyhole size={12} /> Privat
                </span>
              </div>
              <div className="lp-mock__amount">Rp 2.845.000</div>
              <div className="lp-mock__stats">
                <div>
                  <span>Pesanan</span>
                  <strong>68</strong>
                </div>
                <div>
                  <span>Selesai</span>
                  <strong>64</strong>
                </div>
                <div>
                  <span>Antrean</span>
                  <strong>4</strong>
                </div>
              </div>
              <div className="lp-mock__list">
                <div>
                  <span>
                    <span className="lp-dot lp-dot--green" />
                    Meja 04 · 2 item
                  </span>
                  <strong>Rp 86.000</strong>
                </div>
                <div>
                  <span>
                    <span className="lp-dot lp-dot--amber" />
                    Meja 07 · 4 item
                  </span>
                  <strong>Rp 142.000</strong>
                </div>
                <div>
                  <span>
                    <span className="lp-dot lp-dot--green" />
                    Kamar 12 · 1 item
                  </span>
                  <strong>Rp 35.000</strong>
                </div>
              </div>
              <div className="lp-mock__foot">
                <CheckCircle2 size={14} />
                Pembayaran QRIS masuk langsung ke rekening owner
              </div>
            </div>
          </div>
        </section>

        <section className="lp-strip">
          <div className="lp-container lp-strip__grid">
            <div className="lp-strip__item">
              <QrCode size={20} />
              <div>
                <strong>Satu QR, satu meja</strong>
                <span>Meja atau kamar terisi otomatis.</span>
              </div>
            </div>
            <div className="lp-strip__item">
              <ShieldCheck size={20} />
              <div>
                <strong>Dana tidak ditahan</strong>
                <span>QRIS masuk langsung ke rekening owner.</span>
              </div>
            </div>
            <div className="lp-strip__item">
              <Zap size={20} />
              <div>
                <strong>Realtime ke dapur</strong>
                <span>Pesanan tampil begitu pelanggan memesan.</span>
              </div>
            </div>
          </div>
        </section>

        <section id="masalah" className="lp-section">
          <div className="lp-container">
            <div className="lp-section__head">
              <p className="eyebrow">Masalah lama</p>
              <h2>
                Operasional cafe tidak harus <em>begitu-begitu saja</em>.
              </h2>
              <p>
                Banyak usaha F&amp;B tumbuh dengan cara yang melelahkan: antrean
                di kasir, catatan manual, dan tutup toko yang selalu berakhir
                dengan hitung-hitungan. Padahal pelanggan hanya ingin dilayani
                lebih cepat, dan owner hanya ingin tenang.
              </p>
            </div>
            <div className="lp-problem-grid">
              {problems.map((problem) => {
                const Icon = problem.icon;
                return (
                  <article className="lp-problem" key={problem.title}>
                    <span className="lp-problem__icon">
                      <Icon size={18} />
                    </span>
                    <h3>{problem.title}</h3>
                    <p>{problem.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="fitur" className="lp-section lp-section--surface">
          <div className="lp-container">
            <div className="lp-section__head">
              <p className="eyebrow">Satu alur, dari meja ke dapur</p>
              <h2>Yang dibutuhkan usaha kecil, tanpa kerumitan enterprise.</h2>
              <p>
                Cafe System Control menyatukan pemesanan, pembayaran, dan
                operasional harian dalam satu workspace yang tenang dan mudah
                dipakai.
              </p>
            </div>
            <div className="lp-feature-grid">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <article className="lp-feature" key={feature.title}>
                    <span className="lp-feature__icon">
                      <Icon size={19} />
                    </span>
                    <h3>{feature.title}</h3>
                    <p>{feature.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="alur" className="lp-section">
          <div className="lp-container">
            <div className="lp-section__head">
              <p className="eyebrow">Cara kerja</p>
              <h2>Mulai dari QR pertama, selesai dalam hitungan menit.</h2>
              <p>
                Tidak perlu mengganti QRIS, tidak perlu perangkat tambahan.
                Cukup siapkan menu dan meja, lalu biarkan alurnya berjalan.
              </p>
            </div>
            <div className="lp-steps">
              {steps.map((step, index) => (
                <article className="lp-step" key={step.title}>
                  <span className="lp-step__num">{index + 1}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="untuk-siapa" className="lp-section lp-section--surface">
          <div className="lp-container">
            <div className="lp-section__head">
              <p className="eyebrow">Untuk siapa</p>
              <h2>Dibangun untuk usaha yang sibuk, bukan untuk proses yang rumit.</h2>
              <p>
                Fokus kami adalah UMKM F&amp;B skala kecil: cafe, kedai, warung,
                dan pengelola kost yang ingin naik kelas tanpa kehilangan
                kendali atas uang dan privasi usahanya.
              </p>
            </div>
            <div className="lp-audience-grid">
              {audiences.map((audience) => {
                const Icon = audience.icon;
                return (
                  <article className="lp-audience" key={audience.title}>
                    <span className="lp-audience__icon">
                      <Icon size={20} />
                    </span>
                    <h3>{audience.title}</h3>
                    <p>{audience.text}</p>
                    <ul>
                      {audience.points.map((point) => (
                        <li key={point}>
                          <CheckCircle2 size={14} />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="lp-quote">
          <div className="lp-container">
            <blockquote>
              “Penghasilan adalah privasi owner. Antrean bukan cara melayani.
              Dan tutup toko seharusnya tidak berarti <span>hitung-hitungan</span>
              .”
            </blockquote>
          </div>
        </section>

        <section className="lp-cta">
          <div className="lp-container">
            <div className="lp-cta__inner">
              <h2>Siap membuat operasional lebih tenang?</h2>
              <p>
                Mulai dari satu outlet dan satu QR di meja pertama. Pelanggan
                memesan sendiri, dapur menerima order, dan owner melihat
                semuanya dengan jelas.
              </p>
              <div className="lp-cta__actions">
                {me ? (
                  <Link to="/app" className="btn btn-primary">
                    Buka Workspace <ArrowRight size={16} />
                  </Link>
                ) : (
                  <>
                    <Link to="/register" className="btn btn-primary">
                      Daftar Gratis <ArrowRight size={16} />
                    </Link>
                    <Link to="/login" className="btn btn-secondary">
                      Masuk ke Workspace
                    </Link>
                  </>
                )}
              </div>
              <p className="lp-cta__note">
                Tanpa antrean kasir. Tanpa stock opname. Omzet tetap privat.
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-container lp-footer__grid">
          <div>
            <Link
              to="/"
              className="lp-brand lp-brand--footer"
              onClick={handleHomeClick}
            >
              <BrandMark />
              <span>
                <strong>Cafe System Control</strong>
                <small>Platform pemesanan cafe &amp; kost</small>
              </span>
            </Link>
            <p>
              Membantu cafe, kedai, dan kost melayani lebih cepat dengan
              pemesanan QR dan pembayaran QRIS yang langsung masuk ke rekening
              owner.
            </p>
          </div>
          <div>
            <strong>Produk</strong>
            <a href="#fitur">Fitur</a>
            <a href="#alur">Cara kerja</a>
            <a href="#untuk-siapa">Untuk siapa</a>
          </div>
          <div>
            <strong>Akun</strong>
            {me ? <Link to="/app">Workspace</Link> : <Link to="/login">Masuk</Link>}
            {me ? null : <Link to="/register">Daftar gratis</Link>}
            <a href="#masalah">Masalah yang diselesaikan</a>
          </div>
        </div>
        <div className="lp-container lp-footer__bottom">
          <span>© {new Date().getFullYear()} Cafe System Control</span>
          <span>Dibangun untuk cafe, kedai, dan kost.</span>
        </div>
      </footer>
    </div>
  );
}
